
/*!
=========================================================
* Ziro Studio React - v7.0.0 (Modern Production UI)
=========================================================
* Product: Ziro AI Studio
* Copyright (year is dynamic in UI) ZeroUI, Inc.
* 
* Modernized Studio page matching production ziro_studio.html
* Uses Explore.js scaffolding with production chat UI
=========================================================
*/

import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { connect } from "react-redux";
import NotificationAlert from "react-notification-alert";
import { notify, getCookie } from "../Utils";

// reactstrap components (same as Explore.js)
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Button,
  Input,
  Spinner,
} from "reactstrap";

// Components
import ZiroChat from "./ZiroChat";

// Import service layer
import ziroChatInterfaces from "../../services/ziroChatInterfaces";

// Import production styles
import "./StudioChat.css";

/**
 * StudioPage - Modern Chat-First Studio (React Version)
 * 
 * URL: /studio-re/:projectCode
 * 
 * Follows Explore.js scaffolding but with production ziro_studio.html UI
 * Chat-first interface with hidden/toggleable code editor
 */
function StudioPage(props) {
  const { projectCode } = useParams();
  const notificationAlertRef = useRef(null);
  
  // Project and authentication state
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [isLoggedOut, setIsLoggedOut] = useState(false); // Track if logout happened from another window
  
  // Editor state (used by Code Editor panel)
  const [code, setCode] = useState("");
  const [isEditorVisible, setIsEditorVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = () => {
      // Check if logout was triggered from another window
      if (sessionStorage.getItem("logout_in_progress") === "true") {
        console.log("🔒 Logout in progress (from another window), clearing auth state");
        sessionStorage.removeItem("logout_in_progress");
        setIsAuthenticated(false);
        setCheckingAuth(false);
        // Dispatch logout to Redux
        if (props.auth === 1) {
          props.dispatch({ type: "LOGOUT" });
        }
        return false;
      }
      
      const token = getCookie("ziroatoken");
      const userId = localStorage.getItem("google_user_id");
      const loggedIn = localStorage.getItem("loggedin");
      
      console.log("🔍 Studio Auth Check (new window):", { 
        token: !!token, 
        userId, 
        loggedIn,
        hostname: window.location.hostname,
        hasCookie: !!token,
        hasLocalStorage: !!userId && loggedIn === 'true'
      });
      
      if (token && userId && loggedIn === 'true') {
        // User is authenticated
        if (props.auth !== 1) {
          // Sync with Redux
          const userPicture = localStorage.getItem("profile_image_url") || '';
          props.dispatch({ type: "LOGIN" });
          props.dispatch({ type: "USERID", id: userId });
          props.dispatch({ type: "USERIMG", url: userPicture });
          console.log('✅ Synced existing auth to Redux store in new window');
        }
        
        setIsAuthenticated(true);
        setCheckingAuth(false);
        return true;
      }
      
      setIsAuthenticated(false);
      setCheckingAuth(false);
      return false;
    };

    checkAuth();
  }, [props.auth, props.dispatch]);

  // Listen for cross-window logout events
  useEffect(() => {
    // Initialize cross-tab auth sync if available
    if (window.ZiroCrossTabAuthSync && !window.ZiroCrossTabAuthSync.channel) {
      window.ZiroCrossTabAuthSync.initialize();
    }
    
    // Listen for logout events from other windows
    const handleLogoutEvent = (event) => {
      console.log("🚪 Studio page received logout event from another window");
      
      // IMMEDIATELY disable chat and update state (no reload)
      setIsLoggedOut(true);
      setIsAuthenticated(false);
      setCheckingAuth(false);
      
      // Dispatch logout to Redux
      if (props.auth === 1) {
        props.dispatch({ type: "LOGOUT" });
      }
      
      // The cross-tab-auth-sync.js already handles clearing cookies/localStorage
      // Chat is now disabled - system message will be shown by ZiroChat component
    };
    
    // Listen for custom logout event dispatched by cross-tab-auth-sync.js
    window.addEventListener('ziro:logged-out', handleLogoutEvent);
    
    // Cleanup
    return () => {
      window.removeEventListener('ziro:logged-out', handleLogoutEvent);
    };
  }, [props.auth, props.dispatch]);

  // Load project data (works for both authenticated and guest users)
  useEffect(() => {
    if (projectCode) {
      loadProject();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectCode]);


  const loadProject = async () => {
    setIsLoading(true);
    
    try {
      console.log("📂 Loading project:", projectCode);
      const projectData = await ziroChatInterfaces.getProject(projectCode);
      
      if (projectData && projectData.success && projectData.data) {
        // API returns array with project at index 0 (matching production ziro_studio.html)
        const actualProject = Array.isArray(projectData.data) ? projectData.data[0] : projectData.data;
        if (actualProject) {
          setProject(actualProject);
          console.log("✅ Project loaded:", actualProject.title);
          
          // Load code (if authenticated)
          if (isAuthenticated) {
            try {
              const codeData = await ziroChatInterfaces.getCode(projectCode);
              if (codeData && codeData.success && codeData.code) {
                setCode(codeData.code);
                console.log("✅ Code loaded");
              }
            } catch (codeError) {
              console.warn("⚠️ Could not load code:", codeError.message);
              // Continue without code - project still loads
            }
          }
          
          // Welcome message is now handled by ZiroChat component
        }
      }
    } catch (error) {
      console.error("❌ Error loading project:", error);
      notify("error", "Failed to load project. Please try again.", notificationAlertRef);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveCode = async () => {
    // Check authentication first
    if (!isAuthenticated) {
      notify("warning", "Please sign in to save your project.", notificationAlertRef);
      return;
    }
    
    if (!project || isSaving) return;
    
    setIsSaving(true);
    
    try {
      console.log("💾 Saving code using production saveCodeToBackend...");
      
      // Use production saveCodeToBackend function
      await ziroChatInterfaces.saveCodeToBackend(code);
      
      notify("success", "Code saved successfully!", notificationAlertRef);
      console.log("✅ Code saved");
    } catch (error) {
      console.error("❌ Error saving code:", error);
      notify("error", "Failed to save code. Please try again.", notificationAlertRef);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleEditor = () => {
    setIsEditorVisible(!isEditorVisible);
  };

  // Show loading spinner while checking auth (but don't block access)
  if (checkingAuth) {
    return (
      <div className="content">
        <Row>
          <Col md="12" className="text-center" style={{ marginTop: '20vh' }}>
            <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
            <p style={{ marginTop: '20px', color: '#8f9bb3' }}>Loading Studio...</p>
          </Col>
        </Row>
      </div>
    );
  }

  // Note: We allow unauthenticated users to view projects
  // Authentication status is shown in the navbar avatar area

  // Main Studio UI (full-screen chat-first layout)
  return (
    <>
      <div className="react-notification-alert-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <div className="studio-content">
        <Row>
          <Col md="12">
            {/* Chat Component with integrated toolbar */}
            <ZiroChat 
              project={project}
              projectCode={projectCode}
              isLoading={isLoading}
              isAuthenticated={isAuthenticated && !isLoggedOut}
              onToggleEditor={toggleEditor}
              onSaveCode={handleSaveCode}
              isSaving={isSaving}
              isLoggedOut={isLoggedOut}
            />
          </Col>
        </Row>
      </div>

      {/* Code Editor (hidden by default, slides in when visible) */}
      {isEditorVisible && (
        <div className="code-editor-container visible" style={{
          position: 'fixed',
          top: '80px',
          left: 0,
          width: '50%',
          height: 'calc(100% - 80px)',
          backgroundColor: '#1e1e2f',
          borderRight: '2px solid #2d3561',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h4 style={{ color: '#ffffff', margin: 0 }}>Code Editor</h4>
            <Button onClick={toggleEditor} size="sm" style={{ backgroundColor: '#16213e' }}>
              <i className="fa fa-times"></i> Close
            </Button>
          </div>
          <textarea
            value={code}
            onChange={(e) => setCode(e.target.value)}
            style={{
              width: '100%',
              height: 'calc(100% - 80px)',
              backgroundColor: '#272822',
              color: '#f8f8f2',
              border: '1px solid #2d3561',
              borderRadius: '4px',
              padding: '15px',
              fontFamily: '"Courier New", monospace',
              fontSize: '14px',
              resize: 'none'
            }}
          />
          <div style={{ marginTop: '10px', textAlign: 'right' }}>
            <Button 
              onClick={handleSaveCode}
              disabled={isSaving}
              className="button_toolbar"
            >
              <i className="fa fa-save"></i> {isSaving ? 'Saving...' : 'Save Code'}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

// Connect to Redux for auth state (same pattern as Explore.js)
const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(StudioPage);
